import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Paper, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoTooltip from '../../../common_components/InfoTooltip';
import { NETWORK_METADATA_MAP } from '../../../utils/networkConstants';
import { StyledCurrency } from '../../../helper/StyledCurrency';
const MetricRow = ({ label, current, previous, isCurrency = false, isRoas = false, isPercent = false, reverseTrendColors = false }) => {
    const prevNum = previous || 0;
    const currNum = current || 0;
    let pct = 0;
    if (prevNum !== 0) {
        pct = ((currNum - prevNum) / prevNum) * 100;
    }
    else if (currNum > 0) {
        pct = 100;
    }
    const trend = pct === 0 ? "neutral" : pct < 0 ? "down" : "up";
    let isBad = trend === "down";
    if (reverseTrendColors) {
        isBad = trend === "up";
    }
    const badgeColor = isBad ? "#B42318" : "#027A48";
    const badgeBg = isBad ? "#FEF3F2" : "#ECFDF3";
    const formatVal = (val) => {
        if (isRoas)
            return val.toFixed(2);
        if (isPercent)
            return `${val.toFixed(2)}%`;
        if (isCurrency)
            return new Intl.NumberFormat('en-US').format(Math.abs(val));
        return new Intl.NumberFormat('en-US').format(val);
    };
    const hasPrevious = previous !== undefined;
    return (_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #F2F4F7', '&:last-child': { borderBottom: 'none', pb: 0 } }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#101828', width: '25%' }, children: label }), _jsx(Box, { sx: { width: '25%' }, children: _jsx(StyledCurrency, { value: isCurrency ? `$${formatVal(currNum)}` : String(formatVal(currNum)), symbolWeight: 600, valueWeight: 600, fontSize: "13px", symbolSize: "13px", symbolColor: "#98A2B3", valueColor: "#101828" }) }), _jsx(Box, { sx: { width: '25%' }, children: hasPrevious ? (_jsx(StyledCurrency, { value: isCurrency ? `$${formatVal(prevNum)}` : String(formatVal(prevNum)), symbolWeight: 400, 
                    // valueWeight={400}
                    fontSize: "13px", symbolSize: "13px", symbolColor: "#98A2B3", valueColor: "#98A2B3" })) : (_jsx(Typography, { sx: { fontSize: '13px', color: '#98A2B3' }, children: "-" })) }), _jsx(Box, { sx: { width: '25%', display: 'flex', justifyContent: 'flex-end' }, children: hasPrevious && (_jsxs(Box, { sx: {
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: '2px', borderRadius: '16px',
                        bgcolor: badgeBg,
                        color: badgeColor,
                        fontSize: '13px', fontWeight: 500
                    }, children: [_jsx(StyledCurrency, { value: `${Math.round(Math.abs(pct))}%`, valueColor: badgeColor, symbolColor: badgeColor, fontSize: "13px", symbolSize: "13px", valueWeight: 500, symbolWeight: 500 }), trend === "up" ? _jsx(TrendingUpIcon, { sx: { fontSize: 14 } }) : trend === "down" ? _jsx(TrendingDownIcon, { sx: { fontSize: 14 } }) : null] })) })] }));
};
const ChannelReportingTab = ({ data, formula }) => {
    if (!data || Object.keys(data).length === 0) {
        return _jsx(Typography, { sx: { p: 2, color: '#667085' }, children: "No channel data available." });
    }
    return (_jsx(Box, { sx: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }, children: Object.entries(data)
            .sort(([keyA], [keyB]) => {
            const SEQUENCE = ['shopify', 'shopify_net_sale', 'ga', 'meta', 'adword', 'bing', 'criteo', 'channels_revenue'];
            const indexA = SEQUENCE.indexOf(keyA);
            const indexB = SEQUENCE.indexOf(keyB);
            if (indexA === -1 && indexB === -1)
                return keyA.localeCompare(keyB);
            if (indexA === -1)
                return 1;
            if (indexB === -1)
                return -1;
            return indexA - indexB;
        })
            .map(([networkKey, metrics]) => {
            let meta = NETWORK_METADATA_MAP[networkKey] || { name: networkKey, icon: '' };
            if (networkKey === 'shopify') {
                meta = { ...NETWORK_METADATA_MAP['shopify'], name: 'Shopify Revenue' };
            }
            else if (networkKey === 'shopify_net_sale') {
                meta = { ...NETWORK_METADATA_MAP['shopify'], name: 'Shopify Net Sales' };
            }
            const prevRoas = metrics.prev_roas || 0;
            const currRoas = metrics.roas || 0;
            let roasPct = 0;
            if (prevRoas !== 0) {
                roasPct = ((currRoas - prevRoas) / prevRoas) * 100;
            }
            else if (currRoas > 0) {
                roasPct = 100;
            }
            const isRoasDown = metrics.prev_roas !== undefined && metrics.prev_roas !== null && roasPct < 0;
            return (_jsxs(Paper, { elevation: 0, sx: { border: '1px solid #EAECF0', borderRadius: '8px', p: 2 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }, children: [meta.icon && _jsx("img", { src: meta.icon, alt: meta.name, style: { width: 24, height: 24 } }), _jsx(Typography, { sx: { fontWeight: 600, color: '#101828', fontSize: '16px' }, children: meta.name }), _jsx(InfoTooltip, { title: networkKey === 'channels_revenue'
                                    ? 'Sum of all channels'
                                    : (metrics.formula || (networkKey === 'shopify' && formula))
                                        ? `Derived using: (${metrics.formula || formula})`
                                        : `${meta.name} Performance Metrics`, placement: "top", sx: { mt: '2px' } })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, className: "performance-matrics", children: [_jsx(MetricRow, { label: "Revenue", current: metrics.revenue, previous: metrics.prev_revenue, isCurrency: true }), _jsx(MetricRow, { label: "Spend", current: metrics.spend, previous: metrics.prev_spend, isCurrency: true, reverseTrendColors: isRoasDown }), _jsx(MetricRow, { label: "ROAS", current: metrics.roas, previous: metrics.prev_roas, isRoas: true }), _jsx(MetricRow, { label: "MP", current: metrics.mp, previous: metrics.prev_mp, isPercent: true, reverseTrendColors: true })] })] }, networkKey));
        }) }));
};
export default ChannelReportingTab;
